import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Mail, Phone, Smartphone, MapPin } from 'lucide-react';
import Logo from '@/components/Logo';
import { base44 } from '@/api/base44Client';
import { CONTACT } from '@/data/contact';
import { SERVICES } from '@/data/services';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const valid = /\S+@\S+\.\S+/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      await base44.functions.invoke('submitLead', {
        type: 'partner_deck',
        email,
        message: 'Requested the roster deck, rate card and campaign case studies.',
      });
      setSent(true);
    } catch (err) {
      window.location.href = `mailto:${CONTACT.sales}?subject=Partnership enquiry — roster deck & rate card`;
    }
    setBusy(false);
  };

  return (
    <footer id="contact" className="bg-[#0B0B0B] border-t border-white/10">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 pb-14 border-b border-white/10">
          <div>
            <Logo className="h-9" />
            <p className="text-[9px] uppercase tracking-[0.3em] text-white/70 mt-1.5">Agency</p>
            <p className="mt-6 text-white/65 font-light leading-relaxed max-w-md">
              A digital agency representing premium global talent and producing enterprise-grade
              creative work for brands operating at the highest tier.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              {CONTACT.offices.map((o) => (
                <div key={o.label}>
                  <div className="flex items-center gap-2 mb-2.5">
                    <MapPin className="w-3.5 h-3.5 text-[#3B82F6]" />
                    <span className="text-[10px] uppercase tracking-[0.16em] text-white/70">{o.label}</span>
                  </div>
                  <address className="not-italic text-sm text-white/65 leading-relaxed">
                    {o.lines.join(', ')}
                  </address>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
              <p className="text-xs uppercase tracking-[0.14em] text-white/65 leading-relaxed">
                For bookings, talent representations, and enterprise digital production inquiries:
              </p>
              <div className="flex flex-wrap gap-x-7 gap-y-3">
                <a href={CONTACT.officePhoneHref} className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-[#3B82F6] transition-colors">
                  <Phone className="w-4 h-4" /> {CONTACT.officePhone}
                </a>
                <a href={CONTACT.mobilePhoneHref} className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-[#3B82F6] transition-colors">
                  <Smartphone className="w-4 h-4" /> {CONTACT.mobilePhone}
                </a>
                <a href={`mailto:${CONTACT.bookings}`} className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-[#3B82F6] transition-colors">
                  <Mail className="w-4 h-4" /> {CONTACT.bookings}
                </a>
                <a href={`mailto:${CONTACT.sales}`} className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-[#3B82F6] transition-colors">
                  <Mail className="w-4 h-4" /> {CONTACT.sales}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:pl-10 lg:border-l lg:border-white/10">
            <h3 className="font-display font-bold text-xl lg:text-2xl text-white tracking-tight">Partner with Us</h3>
            <p className="mt-3 text-white/65 font-light text-sm leading-relaxed max-w-sm">
              Submit your details to receive our roster deck, rate card, and campaign case studies.
            </p>

            {sent ? (
              <div className="mt-7 flex items-center gap-3 rounded-lg border border-[#3B82F6]/40 bg-[#2563EB]/10 px-5 py-4">
                <Check className="w-5 h-5 text-[#3B82F6] shrink-0" />
                <p className="text-sm text-white/80">
                  Thank you — our partnerships team will reach out to {email} shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-7 flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your business email"
                  className="flex-1 h-14 px-5 rounded-lg bg-white/5 border border-white/15 text-white placeholder:text-white/35 text-sm outline-none focus:border-[#3B82F6] transition-colors"
                />
                <button
                  type="submit"
                  disabled={!valid || busy}
                  className={`inline-flex items-center justify-center gap-2 px-7 h-14 rounded-lg text-sm font-semibold transition-all duration-300 ${
                    valid ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' : 'bg-white/10 text-white/35 cursor-not-allowed'
                  }`}
                >
                  {busy ? 'Sending…' : 'Submit'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}

            <nav className="mt-10 grid grid-cols-2 gap-y-3 gap-x-6 text-sm text-white/65">
              <Link to="/#roster" className="hover:text-white transition-colors">Our Roster</Link>
              <Link to="/#services" className="hover:text-white transition-colors">Services</Link>
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="hover:text-white transition-colors">
                  {s.title.split(' & ')[0]}
                </Link>
              ))}
              <Link to="/huntr" className="hover:text-white transition-colors">Huntr Leads Engine</Link>
              <Link to="/#partnerships" className="hover:text-white transition-colors">Corporate Partnerships</Link>
              <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
              <Link to="/insights" className="hover:text-white transition-colors">Insights</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
            </nav>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-white/65">
            Ahana Digital Agency © 2026. All rights reserved. Global influence, engineered.
          </p>
          <div className="flex flex-wrap gap-6 text-sm text-white/65">
            <Link to="/support" className="hover:text-white transition-colors">Support</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}